import React, { useEffect, useState } from "react";
import { history } from "../../_Bootstrap/RoutingAndStore";
import { CancellablePromise, CancelledPromise } from "../../Utilities/Generic/Promises";
import { getOpenPages } from "../../Workers/Tab/TabEvents";
import LoadingScreen from "../Loading/LoadingScreen";

// Pages that are only allowed to be opened once
export const UNIQUE_PAGES = {
    "/"           : "Sell Screen",
    "/manage-cash": "Manage Cash",
    "/close"      : "Close Register",
};

interface WithUniqueState {
    loading: boolean;
    page: string;
}

export function withUnique<T>(
    WrappedComponent: React.ComponentType<T>
): React.ComponentType<T> {
    return class extends React.Component<T, WithUniqueState> {
        public displayName = "WithUniqueState";
        protected cancellablePromise: null | CancellablePromise<ReturnType<typeof getOpenPages>> = null;

        public state = {
            loading: true,
            page   : "",
        };

        public componentDidMount(): void {
            const page = window.location.pathname as keyof typeof UNIQUE_PAGES;

            if(UNIQUE_PAGES[page]) {
                this.setState({
                    loading: true,
                    page   : UNIQUE_PAGES[page],
                });

                this.cancellablePromise = new CancellablePromise(
                    getOpenPages()
                );

                this.cancellablePromise
                    .promise
                    .catch(err => {
                        if(err.cancelled) {
                            return false;
                        }

                        console.error("Failed to get open pages.", err);

                        return {};
                    })
                    .then(pages => {
                        if(pages === false) {
                            return;
                        }

                        const openPaths = Object.keys(pages);

                        if(
                            typeof window !== "undefined" &&
                            openPaths.find(path => !!UNIQUE_PAGES[path as keyof typeof UNIQUE_PAGES])
                        ) {
                            window.location.reload();
                        } else {
                            this.setState({
                                loading: false,
                            });
                        }
                    });
            } else {
                this.setState({
                    loading: false,
                });
            }
        }

        public componentWillUnmount(): void {
            if(this.cancellablePromise) {
                this.cancellablePromise.cancel();
            }
        }

        public render(): React.ReactNode {
            if(this.state.loading) {
                return (
                    <LoadingScreen>
                        Loading {this.state.page}
                    </LoadingScreen>
                );
            }

            return (
                <WrappedComponent {...this.props} />
            );
        }
    };
}

export const useUnique = (): string | null => {
    const [ loading, setLoading ] = useState(true);
    const [ page, setPage ] = useState("");

    useEffect(() => {
        const page = history.location.pathname as keyof typeof UNIQUE_PAGES;
        let promise: CancellablePromise<ReturnType<typeof getOpenPages>> | undefined;

        if(UNIQUE_PAGES[page]) {
            setLoading(true);
            setPage(page);

            promise = new CancellablePromise(getOpenPages());
            promise
                .catch(err => {
                    if(err instanceof CancelledPromise) {
                        return false;
                    }

                    console.error("Unable to get open pages: ", err);

                    return {};
                })
                .then(pages => {
                    promise = undefined;

                    if(pages === false) {
                        return;
                    }

                    const openPaths = Object.keys(pages);

                    if(
                        typeof window !== "undefined" &&
                        openPaths.find(path => !!UNIQUE_PAGES[path as keyof typeof UNIQUE_PAGES])
                    ) {
                        window.location.reload();
                    } else {
                        setLoading(false);
                    }

                });
        } else {
            setLoading(false);
        }

        return () => {
            if(promise) {
                promise.cancel();
            }
        };
    }, []);

    if(!loading) {
        return null;
    }

    return page;
};
